import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Fab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import RedirectBackButton from "components/RedirectBackButton";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { kebabCase } from "lodash";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Attribute, ResponseOfAttribute } from "services/KnowfullyAdminClient";
import attributeStore from "stores/attributeStore";
import { Severity, SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import {
  attributeTypeConfigs,
  getMetaValueComponent,
  metaValueLabelKeys,
} from "./CustomAttributeComponents";

export const AttributeDetails = () => {
  const { formatMessage: t } = useIntl();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    attributesUrl,
    attributeListUrl,
    attributeEditUrl,
    attributeService,
  } = useGlobalContext();
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [deleting, setDeleting] = useState<Attribute | null>(null);

  const attributeResult = useStrictQuery<ResponseOfAttribute>(
    ["attribute", id],
    () => attributeService.getAttributeById(id!),
    {
      enabled: !!id,
    },
  );

  const attribute = attributeResult.data?.data;
  useTitle(attribute?.value);

  const codeConstant = attributeResult.data?.data?.organizationAttributeType
    ?.attributeType?.codeConstant!;

  const handleDelete = useCallback(async () => {
    try {
      await registerPromise(attributeService.deleteAttribute(deleting?.id!));

      queryClient.invalidateQueries(["getAttributes"]);
      queryClient.invalidateQueries(["skuAttributesByType"]);

      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: "attributeDeleted" }),
          Severity.Warning,
        ),
      );
      setDeleting(null);
      deleteOpenActions.setFalse();

      attributeStore.update((s) => {
        s.organizationAttributeType =
          attributeResult.data?.data?.organizationAttributeType;
      });

      navigate(attributeListUrl(kebabCase(codeConstant)));
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  }, [
    attributeListUrl,
    attributeResult.data?.data?.organizationAttributeType,
    attributeService,
    codeConstant,
    deleteOpenActions,
    deleting?.id,
    navigate,
    queryClient,
    registerPromise,
    t,
  ]);

  if (!id) return <Navigate to={attributesUrl()} replace />;

  const attributeConfig = attributeTypeConfigs.find(
    (c) =>
      c.codeConstant ===
      attributeResult.data?.data?.organizationAttributeType?.attributeType?.codeConstant?.toLowerCase(),
  );

  const showDisplayText =
    attribute?.organizationAttributeType?.attributeType?.hasDisplayText !== false;
  const showSequence =
    attribute?.organizationAttributeType?.attributeType?.hasSequence !== false;

  const metaValueRows = attributeConfig?.metaValues
    .map((metaValueName) => {
      const config = getMetaValueComponent(metaValueName);
      if (!config) return null;
      const { Preview } = config;
      const labelKey = metaValueLabelKeys[metaValueName] ?? metaValueName;
      const hasValue = attribute?.attributeMetaValues?.find(
        (m) => m.name === metaValueName,
      )?.value;
      if (!hasValue) return null;
      return {
        title: t({ id: labelKey }),
        component: (
          <Preview
            metaValueName={metaValueName}
            displayText={attribute?.displayText}
            attributeMetaValues={attribute?.attributeMetaValues}
          />
        ),
      };
    })
    .filter(Boolean) as { title: string; component: React.ReactNode }[] ?? [];

  const rows = [
    {
      title: t({ id: "organization" }),
      component: attribute?.organizationAttributeType?.organizationName && (
        <span>{attribute?.organizationAttributeType?.organizationName}</span>
      ),
    },
    showDisplayText && {
      title: t({ id: "displayText" }),
      component: attribute?.displayText && <span>{attribute.displayText}</span>,
    },
    {
      title: t({ id: "valueLabel" }),
      component: attribute?.value && <span>{attribute.value}</span>,
    },
    showSequence && {
      title: t({ id: "sequenceLabel" }),
      component: attribute?.sequence && <span>{attribute.sequence}</span>,
    },
    ...metaValueRows,
  ].filter((r) => !!r && !!r.component) as { title: string; component: React.ReactNode }[];

  return (
    <>
      <RedirectBackButton
        redirectRoute={attributeListUrl(kebabCase(codeConstant))}
        labelKey="backToListButtonLabel"
      />
      <Box mb={3} display="flex" justifyContent="center">
        <Box ml={4} />
        <Link to={attributeEditUrl(id)} style={{ textDecoration: "none" }}>
          <Fab color="primary" data-cy="editButton">
            <EditIcon />
          </Fab>
        </Link>
        <Box ml={4} />
        <Fab
          onClick={() => {
            setDeleting(attributeResult.data?.data!);
            deleteOpenActions.setTrue();
          }}
        >
          <DeleteIcon />
        </Fab>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {rows.map(({ title, component }, i) => (
              <TableRow key={i}>
                <TableCell
                  component="th"
                  scope="row"
                  style={{ fontWeight: "bold" }}
                >
                  {title}
                </TableCell>
                <TableCell>{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DeleteEntityDialog
        dialogTitleKey={"attributeDeleteTitle"}
        contentKey={"attributeDeleteQuestion"}
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};
